import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/vue-query";
import axios from "axios";
import {
  type ConnectOrderToUserRequest,
  OrdersApi as FinanceOrdersApi,
  type OrdersOrderIdPaymentsPostRequest,
  type PaymentsVerificationPostRequest,
  TransactionsApi,
  UserOrdersApi,
} from "mws-finance-ts-sdk";
import {
  MeApi,
  type MeApiV2MeOrdersGetRequest,
  OrderPaymentStatus,
  OrdersApi,
} from "mws-ts-v2-sdk";

let financeOrdersApi: FinanceOrdersApi;
let ordersApi: OrdersApi;
let transactionsApi: TransactionsApi;
let userOrdersApi: UserOrdersApi;
let meApi: MeApi;

const getMeApi = () => {
  // reduce memory overhead by only using one instance
  if (!meApi) {
    meApi = new MeApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      axios,
    );
  }

  return meApi;
};

const getFinanceOrdersApi = () => {
  // reduce memory overhead by only using one instance
  if (!financeOrdersApi) {
    financeOrdersApi = new FinanceOrdersApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      axios,
    );
  }

  return financeOrdersApi;
};

const getUserOrdersApi = () => {
  // reduce memory overhead by only using one instance
  if (!userOrdersApi) {
    userOrdersApi = new UserOrdersApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      axios,
    );
  }

  return userOrdersApi;
};

const getOrdersApi = () => {
  // reduce memory overhead by only using one instance
  if (!ordersApi) {
    ordersApi = new OrdersApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
    );
  }

  return ordersApi;
};

const getTransactionsApi = () => {
  // reduce memory overhead by only using one instance
  if (!transactionsApi) {
    transactionsApi = new TransactionsApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
    );
  }

  return transactionsApi;
};

type OrderDetailsQueryPayload = {
  orderId: MaybeRef<string>;
  platformId: MaybeRef<string>;
};

export const orderQueryKeys = createQueryKeys("orders", {
  details: (payload: OrderDetailsQueryPayload) => ({
    queryKey: [payload.orderId],
    queryFn: async () => {
      const response = await getOrdersApi().v2OrdersOrderIdGet({
        orderId: unref(payload.orderId),
        xPlatformKey: unref(payload.platformId),
      });

      return response.data;
    },
  }),
  listForCurrentUser: (
    userId: MaybeRef<string>,
    requestParameters: MeApiV2MeOrdersGetRequest,
  ) => ({
    queryKey: [userId],
    queryFn: async () => {
      const response = await getMeApi().v2MeOrdersGet(requestParameters);

      return response.data;
    },
  }),
  orderInvoice: (orderId: MaybeRefOrGetter<string>, platformId: string) => ({
    queryKey: [orderId],
    queryFn: async () => {
      const response = await getUserOrdersApi().ordersOrderIdInvoicePdfGet({
        orderId: toValue(orderId),
        xPlatformKey: platformId,
      });

      return response.data;
    },
  }),
  bundling: (currencyCode: MaybeRef<string>) => ({
    queryKey: [currencyCode],
    queryFn: async () => {
      const response = await getMeApi().v2MeBundlingGet({
        currencyCode: unref(currencyCode),
      });

      return response.data;
    },
  }),
});

export const useInfiniteMyOrdersQuery = (
  pageSize: MaybeRefOrGetter<number>,
  statusParam: MaybeRef<Array<OrderPaymentStatus> | undefined>,
  sortOptions: MaybeRef<{
    sortBy: "DateCreated" | "DateShipped" | "DatePaid" | undefined;
    sortDirection: "ascending" | "descending" | undefined;
  }>,
) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  return useInfiniteQuery({
    queryKey: ["me", "orders", "infiniteList", statusParam],
    queryFn: async ({ pageParam }) => {
      const response = await getMeApi().v2MeOrdersGet({
        xPlatformKey: unref(platformId),
        pageSize: toValue(pageSize),
        offset: pageParam,
        statuses: unref(statusParam),
        sort: unref(sortOptions).sortBy,
        sortBy: unref(sortOptions).sortDirection,
      });

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (toValue(pageSize) || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
    select: (data) => {
      const flattenedOrdersList =
        data.pages.flatMap((page) => page.results) ?? [];

      return {
        data: flattenedOrdersList,
        totalCount: data.pages?.[0]?.totalCount ?? 0,
      };
    },
  });
};

export const useCreatePaymentSessionForOrderMutation = () =>
  useMutation({
    mutationFn: async ({
      orderId,
      platformId,
      ...request
    }: {
      orderId: string;
      platformId: string;
    } & OrdersOrderIdPaymentsPostRequest) => {
      const response = await getTransactionsApi().ordersOrderIdPaymentsPost({
        orderId,
        xPlatformKey: platformId,
        ordersOrderIdPaymentsPostRequest: request,
      });

      return response.data;
    },
  });

export const useCreatePaymentSessionForVerificationMutation = () =>
  useMutation({
    mutationFn: async (request: PaymentsVerificationPostRequest) => {
      const response = await getTransactionsApi().paymentsVerificationPost({
        paymentsVerificationPostRequest: request,
      });

      return response.data;
    },
  });

export const useLinkAccountToOrderMutation = () => {
  const mutationFn = (request: {
    orderId: string;
    data?: ConnectOrderToUserRequest;
  }) => {
    return getFinanceOrdersApi().ordersOrderIdUserPut({
      orderId: request.orderId,
      ordersOrderIdUserPutRequest: request.data,
    });
  };

  return useMutation({ mutationFn });
};

export const useUpdateOrderStatusToOpenMutation = () => {
  const queryClient = useQueryClient();
  const platformStore = usePlatformStore();
  const platformId = platformStore.getPlatformConfiguration.platformId;

  return useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      return await getOrdersApi().v2OrdersOrderIdOpenPatch({ orderId });
    },
    onSuccess(_, { orderId }) {
      queryClient.invalidateQueries({
        queryKey: orderQueryKeys.details({
          orderId,
          platformId,
        }).queryKey,
      });
    },
  });
};

export const usePatchOrderToClaimMutation = () => {
  return useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      return await getUserOrdersApi().ordersOrderIdClaimPatch({ orderId });
    },
  });
};
